import { avatarInitials } from "@serophin/shared";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

type AvatarProps = {
  name?: string;
  email?: string;
  hue?: number;
  src?: string;
  size?: AvatarSize;
  title?: string;
  class?: string;
};

const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "w-5 h-5 text-[10px]",
  sm: "w-6 h-6 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-base",
};

const hueFromName = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) % 360;
  }
  return hash;
};

const AVATAR_SATURATION = 55;
const AVATAR_MIN_LIGHTNESS = 20;
const AVATAR_MAX_LIGHTNESS = 50;
const AVATAR_MIN_CONTRAST = 4.5;

const channelLuminance = (value: number): number => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);

const lightnessLuminance = (hue: number, lightness: number): number => {
  const saturation = AVATAR_SATURATION / 100;
  const level = lightness / 100;
  const chroma = saturation * Math.min(level, 1 - level);
  const channel = (offset: number) => {
    const position = (offset + hue / 30) % 12;
    return level - chroma * Math.max(-1, Math.min(position - 3, Math.min(9 - position, 1)));
  };
  return 0.2126 * channelLuminance(channel(0)) + 0.7152 * channelLuminance(channel(8)) + 0.0722 * channelLuminance(channel(4));
};

const contrastWithWhite = (hue: number, lightness: number): number => 1.05 / (lightnessLuminance(hue, lightness) + 0.05);

const lightnessForHue = (hue: number): number => {
  let low = AVATAR_MIN_LIGHTNESS;
  let high = AVATAR_MAX_LIGHTNESS;
  for (let i = 0; i < 10; i++) {
    const middle = (low + high) / 2;
    if (contrastWithWhite(hue, middle) >= AVATAR_MIN_CONTRAST) {
      low = middle;
    } else {
      high = middle;
    }
  }
  return Math.floor(low);
};

export const Avatar = ({ name, email, hue, src, size = "md", title, class: className }: AvatarProps) => {
  const label = title ?? name ?? email ?? "User";
  const sizeClass = SIZE_CLASSES[size];
  const baseClass =
    `inline-flex items-center justify-center shrink-0 rounded-full ring-1 ring-black/5 dark:ring-white/10 overflow-hidden ${sizeClass} ${className ?? ""}`.trim();

  if (src) {
    return <img src={src} alt={label} title={title ?? label} class={baseClass} />;
  }

  const resolvedHue = hue ?? hueFromName(name || email || "?");
  const style = { backgroundColor: `hsl(${resolvedHue}, ${AVATAR_SATURATION}%, ${lightnessForHue(resolvedHue)}%)` };

  return (
    <span role="img" aria-label={label} title={title ?? label} class={`${baseClass} font-medium text-white`} style={style}>
      {avatarInitials(name, email)}
    </span>
  );
};
